/**
 * Backend'den gelen ham hata mesajlarını yazılım bilmeyen kullanıcının
 * okuyabileceği sade Türkçe cümleye çevirir. NestJS exception isimlerini,
 * class-validator çıktılarını ve Prisma hata kalıplarını yakalar.
 *
 * Pattern öncelik sırası — ilk eşleşen kazanır:
 *   1. statusCode 401/403 (kimlik/yetki)
 *   2. validation pattern (class-validator: "should not exist", "must be", "is required", ...)
 *   3. "Bad Request Exception" başlığı
 *   4. not found (404 veya metin)
 *   5. conflict / unique constraint (409 veya metin)
 *   6. Prisma çağrısı / P-code stack trace
 *   7. statusCode >= 500
 *   8. fallback: ham mesajın güvenli kısaltması; o da boşsa generic
 */
export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

const PRISMA_PATTERN = /(Invalid `?this\.prisma\.|prisma\.|PrismaClient|P\d{4})/i;
const VALIDATION_PATTERN =
  /(should not exist|must be|is required|must not be empty|must match|invalid (email|phone|uuid|format)|each value|property .* should not exist)/i;
const NOT_FOUND_PATTERN = /(not\s*found|notfound|does not exist)/i;
const CONFLICT_PATTERN = /(conflict|already exists|unique constraint|duplicate)/i;
const BAD_REQUEST_PATTERN = /Bad\s*Request\s*Exception/i;

const FALLBACK_MAX_LEN = 80;

function safeTrim(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

export function humanizeBackendError(
  rawMessage: string | null | undefined,
  statusCode: number | null | undefined,
  t: TranslateFn,
): string {
  const code = typeof statusCode === "number" ? statusCode : null;
  const msg = typeof rawMessage === "string" ? rawMessage : "";
  const trimmed = safeTrim(msg);

  if (code === 401) return t("logs.errorPatterns.unauthorized");
  if (code === 403) return t("logs.errorPatterns.forbidden");
  if (code === 429) return t("logs.errorPatterns.tooManyAttempts");

  if (trimmed && VALIDATION_PATTERN.test(trimmed)) {
    return t("logs.errorPatterns.validationFailed");
  }

  if (BAD_REQUEST_PATTERN.test(trimmed) || code === 400) {
    return t("logs.errorPatterns.badRequest");
  }

  if (code === 404 || (trimmed && NOT_FOUND_PATTERN.test(trimmed))) {
    return t("logs.errorPatterns.notFound");
  }

  if (code === 409 || (trimmed && CONFLICT_PATTERN.test(trimmed))) {
    return t("logs.errorPatterns.conflict");
  }

  if (trimmed && PRISMA_PATTERN.test(trimmed)) {
    return t("logs.errorPatterns.prismaUnknown");
  }

  if (code !== null && code >= 500) {
    return t("logs.errorPatterns.internalError");
  }

  if (trimmed) {
    // Tek satır + güvenli uzunluk. Stack trace gibi çok satırlı içerikler
    // ham olarak gösterilmemeli.
    const oneLine = trimmed.split("\n")[0] ?? trimmed;
    return oneLine.length > FALLBACK_MAX_LEN
      ? `${oneLine.slice(0, FALLBACK_MAX_LEN).trim()}…`
      : oneLine;
  }

  return t("logs.errorPatterns.internalError");
}
